import Dialog from "../dialogs/Dialog";
import {
    AppBar,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select, SelectChangeEvent,
    Switch,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import {MarshalData} from "../../fstn/marshal";
import {ActionFactory} from "../../fstn/actionFactory";
import React, {ReactElement, useState} from "react";
import {ParameterDescription} from "../../fstn/action";
import {RegisterValue} from "../../fstn/register";
import {MarshalValueData, RegisterReference} from "../../fstn/marshalValue";
import {Transition} from "../../fstn/transition";
import {Network} from "../../fstn/network";
import {ProjectDetails} from "../../redux/types/firebase";
import SelectMarshalVideo from "./SelectMarshalVideo";
import SelectMarshalAudio from "./SelectMarshalAudio";
import SelectMarshalImage from "./StateMarshalImage";

interface ActionParameterProps {
    parameters: {[key: string]: MarshalValueData}
    marshalValue: MarshalValueData
    name: string
    onChangeReference: (key: string, reference: RegisterReference | undefined) => void
    onChangeValue: (key: string, value: RegisterValue | undefined) => void
    parameter: ParameterDescription
    project: string
    projectDetails: ProjectDetails
    network: Network
    transitions: Array<Transition>
}
function ActionParameter({parameters, marshalValue, name, network, onChangeReference, onChangeValue,
                             parameter, project, projectDetails, transitions}: ActionParameterProps) {
    const value = marshalValue === undefined ? parameter.defaultValue : marshalValue.value;
    const reference = (marshalValue === undefined || marshalValue.reference === undefined) ? [] : marshalValue.reference;
    const [updating, setUpdating] = useState<RegisterValue | undefined>(value);
    const [useReference, setUseReference] = useState(marshalValue?.reference !== undefined);
    const renderSelected = (selected: any) => selected === "" ? <em>Add Action</em> : selected as string;
    const handleInputFocus = ({currentTarget}: React.FocusEvent<HTMLInputElement>) => {
        setUpdating(currentTarget.value);
    };
    const handleInputBlur = () => {
        if (updating === undefined) return;
        if (parameter.type === "number") {
            const number = Number.parseFloat(updating as string);

            if (Number.isNaN(number)) {
                onChangeValue(name, undefined);
            } else {
                onChangeValue(name, number);
            }
        } else if (parameter.type === "boolean") {
            onChangeValue(name, updating === "true");
        } else {
            onChangeValue(name, updating);
        }
    };
    const handleValueChange = ({currentTarget}: React.ChangeEvent<HTMLInputElement>) => {
        setUpdating(currentTarget.value);
    };
    const handleCheckedChange = ({currentTarget}: React.ChangeEvent<HTMLInputElement>) => {
        onChangeValue(name, currentTarget.checked);
    };
    const handleSelectChange = ({target}: SelectChangeEvent) => {
        onChangeValue(name, target.value);
    }
    const handleReferenceChange = () => setUseReference(! useReference);
    const handleReferenceSelect = ({target}: SelectChangeEvent) => {
        onChangeReference(name, [target.value])
    }
    type createSelector = (value: RegisterValue, parameter: ParameterDescription) => ReactElement;
    const selectors: {[key: string]: createSelector} = {
        boolean: (value: RegisterValue, parameter) => (
            <FormControlLabel title={parameter.description} control={
                <Checkbox checked={value as boolean || false} onChange={handleCheckedChange}/>
            } label={name}/>
        ),
        image: (value: RegisterValue, parameter) => (
            <SelectMarshalImage
                name={name} folder="stills" onChangeValue={onChangeValue} parameter={parameter}
                projectDetails={projectDetails} source={`gs://content.flavourworks.co/${project}/images/stills`}
                value={value}/>
        ),
        number: (value: RegisterValue, parameter) => (
            <TextField title={parameter.description} type="number" fullWidth label={name} value={updating}
                        onBlur={handleInputBlur} onFocus={handleInputFocus} onChange={handleValueChange}/>
        ),
        select: (value: RegisterValue, parameter) => (
            <FormControl fullWidth>
                <InputLabel id={parameter.description}>{name}</InputLabel>
                <Select
                    labelId={parameter.description} title={parameter.description} fullWidth value={value as string}
                    displayEmpty renderValue={renderSelected} onChange={handleSelectChange}>
                    <MenuItem value="" disabled>
                        <em>Select value</em>
                    </MenuItem>
                    {(parameter.type as string []).map(choice => (
                        <MenuItem key={choice} value={choice}>{choice}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        ),
        string: (value: RegisterValue, parameter) => (
            <TextField title={parameter.description} fullWidth label={name} value={updating}
                       onBlur={handleInputBlur} onFocus={handleInputFocus} onChange={handleValueChange}/>
        ),
        texture: (value: RegisterValue, parameter) => (
            <SelectMarshalImage
                name={name} folder="textures" onChangeValue={onChangeValue} parameter={parameter}
                projectDetails={projectDetails} source={`gs://content.flavourworks.co/${project}/images/textures`}
                value={value}/>
        ),
        trigger: (value: RegisterValue, parameter) => (
            <FormControl fullWidth>
                <InputLabel id={parameter.description}>{name}</InputLabel>
                <Select title={parameter.description} fullWidth value={value as string} displayEmpty
                        renderValue={renderSelected} onChange={handleSelectChange}>
                    {transitions.map((transition, index) => (
                        <MenuItem key={index} value={transition.trigger}>{transition.trigger}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        ),
        video: (value: RegisterValue, parameter) => (
            <SelectMarshalVideo
                name={name} onChangeValue={onChangeValue} projectDetails={projectDetails}
                parameter={parameter} source={`gs://content.flavourworks.co/${project}/videos`} value={value}/>
        ),
        audio: (value: RegisterValue, parameter) => (
            <SelectMarshalAudio
                name={name} onChangeValue={onChangeValue} projectDetails={projectDetails} parameter={parameter}
                source={`gs://content.flavourworks.co/${project}/audio`} value={value}/>
        )
    };
    const getSelectorElement = (value: RegisterValue | undefined, parameter: ParameterDescription): ReactElement | null => {
        if (Array.isArray(parameter.type)) {
            return selectors.select(value as string, parameter);
        } else if (selectors[parameter.type as string]) {
            return selectors[parameter.type as string](value as string, parameter);
        } else if (parameters[parameter.type]) {
            const referenced = parameters[parameter.type].reference;

            if (referenced && referenced.length > 0 && selectors[referenced[0]]) {
                // If the registers had a type then we could fill something out appropriately here.
            }
        }
        return null;
    }
    const select = getSelectorElement(value, parameter);
    const selectReference = (
        <FormControl fullWidth>
            <InputLabel id={parameter.description}>{name}</InputLabel>
            <Select title={parameter.description} fullWidth value={reference[0] || ""} displayEmpty
                    onChange={handleReferenceSelect}>
                <MenuItem value="" disabled>
                    <em>Select value</em>
                </MenuItem>
                {Array.from(network.registers.keys()).map(register => (
                    <MenuItem key={register} value={register}>{register}</MenuItem>
                ))}
            </Select>
        </FormControl>);

    return (
        <Box m={1} p={1} border={1} borderRadius="4px">
            <FormControlLabel
                control={<Switch checked={useReference} onChange={handleReferenceChange}/>} label={"Reference"}/>
            {useReference ? selectReference : select}
        </Box>
    );
}


const useMarshalStyles = makeStyles(theme => ({
    parameters: {
        overflowY: "scroll",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
}));

interface Props {
    factory: ActionFactory
    marshal: MarshalData
    onChangeReference: (key: string, value: RegisterReference | undefined) => void
    onChangeValue: (key: string, value: RegisterValue | undefined) => void
    onClose: () => void
    network: Network
    project: string
    projectDetails: ProjectDetails
    transitions: Array<Transition>,
}
export default function MarshalDetails({factory, marshal, network, project, projectDetails, transitions,
                                           onChangeReference, onChangeValue, onClose}: Props) {
    const classes = useMarshalStyles();
    const action = factory.get(marshal.action);

    if (action === undefined) return null;
    return (
        <Dialog onClose={onClose}>
            <AppBar position="static">
                <Toolbar variant="dense">
                    <Box flexGrow={1}><Typography>{marshal.action}</Typography></Box>
                </Toolbar>
            </AppBar>
            <Box p={2} width={500} className={classes.parameters}>
                <Typography><em>{action.details.description}</em></Typography>
                {Object.keys(action.details.parameters || {}).map(key => (
                    <ActionParameter
                        key={key} name={key} parameter={action.details.parameters[key]} project={project}
                        network={network} transitions={transitions} marshalValue={marshal.parameters[key]}
                        projectDetails={projectDetails} parameters={marshal.parameters}
                        onChangeReference={onChangeReference} onChangeValue={onChangeValue}/>
                ))}
            </Box>
        </Dialog>
    );
}